import { cn } from "@/lib/utils";

interface FormDisplayProps {
  form: string | ('W' | 'D' | 'L')[];
  variant?: 'letters' | 'dots';
  size?: 'sm' | 'md';
  padTo?: number;
  className?: string;
}

/**
 * Displays a form string (e.g., "WDLDW") with color-coded letters or dots.
 * Form is displayed left-to-right: oldest result first, latest result on the right.
 */
export function FormDisplay({ form, variant = 'letters', size = 'md', padTo, className }: FormDisplayProps) {
  const formArray = typeof form === 'string' ? form.split('') : form;
  
  if (formArray.length === 0 && !padTo) {
    return <span className="text-muted-foreground">-</span>;
  }

  // Calculate placeholder slots needed at the start (oldest positions)
  const paddingCount = padTo ? Math.max(0, padTo - formArray.length) : 0;
  
  // Create array with placeholders + actual results
  const displayArray: (string | null)[] = [
    ...Array(paddingCount).fill(null),
    ...formArray
  ];

  const sizeClasses = {
    sm: variant === 'letters' ? 'w-4 h-4 text-[10px]' : 'w-2 h-2',
    md: variant === 'letters' ? 'w-5 h-5 text-[10px]' : 'w-3 h-3',
  };

  const getResultColor = (result: string, isLetter: boolean) => {
    if (isLetter) {
      switch (result) {
        case 'W': return 'bg-emerald-500 text-white';
        case 'D': return 'bg-amber-500 text-white';
        case 'L': return 'bg-destructive text-destructive-foreground';
        default: return 'bg-muted text-muted-foreground';
      }
    } else {
      switch (result) {
        case 'W': return 'bg-emerald-500';
        case 'D': return 'bg-amber-500';
        case 'L': return 'bg-destructive';
        default: return 'bg-muted';
      }
    }
  };

  if (variant === 'dots') {
    return (
      <div className={cn("flex items-center gap-0.5", className)}>
        {displayArray.map((result, i) => (
          <span
            key={i}
            className={cn(
              "rounded-full",
              sizeClasses[size],
              result === null 
                ? "bg-muted/30 border border-muted-foreground/20"
                : getResultColor(result, false)
            )}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      {displayArray.map((result, i) => (
        <span
          key={i}
          className={cn(
            "rounded font-bold flex items-center justify-center",
            sizeClasses[size],
            result === null 
              ? "bg-muted/30 border border-muted-foreground/20"
              : getResultColor(result, true)
          )}
        >
          {result !== null ? result : ''}
        </span>
      ))}
    </div>
  );
}
